import handleError from '@fuse/core/errorHandler';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useMessage from 'src/app/hooks/useMessage';
import {
  useCreateTemplateCategoryMutation,
  useCreateTemplateSubCategoryMutation,
  useUpdateTemplateCategoryMutation,
  useUpdateTemplateSubCategoryMutation,
} from 'src/app/services/claim/claimApi';

const DocumentCategoryForm = ({ selectedItem, onClose, categories = [] }) => {
  const { t } = useTranslation('translations');
  const { showMessage } = useMessage();

  const [createCategory] = useCreateTemplateCategoryMutation();
  const [updateCategory] = useUpdateTemplateCategoryMutation();
  const [createSub] = useCreateTemplateSubCategoryMutation();
  const [updateSub] = useUpdateTemplateSubCategoryMutation();

  const flattenSubs = (nodes = [], depth = 0, acc = []) => {
    for (const n of nodes) {
      acc.push({ ...n, depth });
      if (n.children?.length) flattenSubs(n.children, depth + 1, acc);
    }
    return acc;
  };

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      nameUz: '',
      nameRu: '',
      templateCategoryId: null, // main category id
      parentId: 0, // subcategory id (0 => no parent)
    },
  });

  const selectedMainId = watch('templateCategoryId');

  useEffect(() => {
    if (selectedItem) {
      reset({
        nameUz: selectedItem.nameUz || '',
        nameRu: selectedItem.nameRu || '',
        // Normalize: for main category it will be null, for sub it will be number
        templateCategoryId:
          selectedItem.templateCategoryId !== undefined && selectedItem.templateCategoryId !== null
            ? Number(selectedItem.templateCategoryId)
            : null,
        parentId: selectedItem.parentId != null ? Number(selectedItem.parentId) : 0,
      });
    } else {
      reset({
        nameUz: '',
        nameRu: '',
        templateCategoryId: null,
        parentId: 0,
      });
    }
  }, [selectedItem, reset]);

  // Parent options from ONLY selected main category (all depths)
  const parentOptions = useMemo(() => {
    if (!selectedMainId) return [];
    const main = categories.find((c) => Number(c.id) === Number(selectedMainId));
    return flattenSubs(main?.children ?? []);
  }, [categories, selectedMainId]);

  const onSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        templateCategoryId: values.templateCategoryId ? Number(values.templateCategoryId) : null,
        parentId: Number(values.parentId ?? 0),
      };

      if (selectedItem?.id) {
        // EDIT
        if (selectedItem.templateCategoryId) {
          await updateSub({ id: selectedItem.id, body: payload }).unwrap();
          showMessage({ message: t('SuccessfullyUpdated'), variant: 'success' });
        } else {
          await updateCategory({ id: selectedItem.id, body: payload }).unwrap();
          showMessage({ message: t('SuccessfullyUpdated'), variant: 'success' });
        }
      } else {
        // CREATE
        if (payload.templateCategoryId) {
          await createSub(payload).unwrap();
          showMessage({ message: t('SuccessfullyAdded'), variant: 'success' });
        } else {
          await createCategory(payload).unwrap();
          showMessage({ message: t('SuccessfullyAdded'), variant: 'success' });
        }
      }

      onClose();
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        {selectedItem?.id ? t('Edit') : t('Create')}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="nameUz"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField {...field} fullWidth label={t('NameUz')} sx={{ mb: 2 }} />
          )}
        />

        <Controller
          name="nameRu"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField {...field} fullWidth label={t('NameRu')} sx={{ mb: 2 }} />
          )}
        />

        {/* MAIN CATEGORY */}
        <Controller
          name="templateCategoryId"
          control={control}
          render={({ field }) => (
            <TextField
              select
              fullWidth
              label={t('MainCategory')}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              value={field.value ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? null : Number(e.target.value);
                field.onChange(v);
                // reset parent when switching main category manually
                setValue('parentId', 0);
              }}
              // Optional: when creating subcategory from tree, lock main category to avoid mistakes
              disabled={Boolean(selectedItem?.templateCategoryId) && !selectedItem?.id}
            >
              <MenuItem value="">{t('NoMainCategory')}</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={Number(c.id)}>
                  {c.nameRu}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Controller
          name="parentId"
          control={control}
          render={({ field }) => (
            <TextField
              select
              fullWidth
              label={t('Subcategory')}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              value={Number(field.value ?? 0)}
              onChange={(e) => field.onChange(Number(e.target.value))}
              disabled={!selectedMainId}
            >
              {parentOptions.length === 0 && <MenuItem value={0}>{t('NoParent')}</MenuItem>}

              {parentOptions.map((sub) => (
                <MenuItem key={sub.id} value={Number(sub.id)}>
                  {`${sub.nameRu}`}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Box className="flex justify-end mt-20">
          <Button onClick={onClose} sx={{ mr: 2 }}>
            {t('Cancel')}
          </Button>

          <Button type="submit" variant="contained" color="secondary">
            {t('Save')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default DocumentCategoryForm;
